import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { LineShortener } from '@elfsight/widget-ui';
import { createReadableClass, useResizeObserver } from '@elfsight/widget-utils';

interface TextShortenerProps {
  text: string;
  messageSeeMoreButton: string;
}

export const TextShortener = ({
  text,
  messageSeeMoreButton
}: TextShortenerProps) => {
  const [descriptionShortened, setDescriptionShortened] = useState(true);
  const [isShorteningPossible, setIsShorteningPossible] = useState(true);

  const textRef = useRef<HTMLDivElement>(null);

  const [rect] = useResizeObserver(textRef.current, {
    throttleMs: 200
  });

  useEffect(() => {
    if (!textRef.current) {
      return;
    }

    const isOverflowing =
      textRef.current.scrollWidth > textRef.current.clientWidth;
    setIsShorteningPossible(isOverflowing);
  }, [rect]);

  const showFullText = () => {
    setDescriptionShortened(false);
    setIsShorteningPossible(false);
  };

  return (
    <Container>
      <LineShortener shortenedLines={1} shortened={descriptionShortened}>
        <Content ref={textRef} _descriptionShortened={descriptionShortened}>
          {text}
        </Content>
      </LineShortener>

      {isShorteningPossible && (
        <Control onClick={showFullText}>{messageSeeMoreButton}</Control>
      )}
    </Container>
  );
};

const Control = styled.div.attrs({
  className: createReadableClass({
    component: 'text-shortener',
    element: 'control'
  })
})`
  cursor: pointer;
  color: #999;

  &:hover,
  &:focus {
    text-decoration: underline;
  }
`;

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'text-shortener',
    element: 'container'
  })
})`
  display: flex;
  font-size: 14px;
  line-height: 18px;
`;

const Content = styled.div.attrs({
  className: createReadableClass({
    component: 'text-shortener',
    element: 'content'
  })
})<{
  _descriptionShortened: boolean;
}>`
  word-break: break-word;
  overflow-wrap: break-word;

  ${({ _descriptionShortened }) =>
    _descriptionShortened &&
    css`
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    `}
`;
